export interface CurvePoint {
  readonly x: number
  readonly y: number
}

export interface CurveSegment {
  readonly from: CurvePoint
  readonly fromControl: CurvePoint
  readonly toControl: CurvePoint
  readonly to: CurvePoint
}

const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value))

/**
 * A monotone cubic spline through the points. Its handles stay inside each segment in both
 * dimensions: a distant future day cannot pull an earlier handle backward, and smoothing can
 * never invent a value above or below the two days being joined.
 */
export function curveSegments(points: readonly CurvePoint[]): CurveSegment[] {
  if (points.length < 2) {
    return []
  }

  const pairs: { from: CurvePoint; to: CurvePoint }[] = []
  for (let index = 1; index < points.length; index++) {
    const from = points[index - 1]
    const to = points[index]
    if (from && to) {
      pairs.push({ from, to })
    }
  }
  const widths = pairs.map(({ from, to }) => to.x - from.x)
  const slopes = pairs.map(({ from, to }, index) => {
    const width = widths[index] ?? 0
    return width === 0 ? 0 : (to.y - from.y) / width
  })
  const tangents = Array.from({ length: points.length }, () => 0)
  tangents[0] = slopes[0] ?? 0
  tangents[tangents.length - 1] = slopes[slopes.length - 1] ?? 0

  // A weighted harmonic mean follows both neighboring slopes without crossing either one.
  for (let index = 1; index < points.length - 1; index++) {
    const before = slopes[index - 1] ?? 0
    const after = slopes[index] ?? 0
    if (before * after <= 0) {
      tangents[index] = 0
      continue
    }
    const beforeWidth = widths[index - 1] ?? 0
    const afterWidth = widths[index] ?? 0
    const beforeWeight = 2 * afterWidth + beforeWidth
    const afterWeight = afterWidth + 2 * beforeWidth
    tangents[index] = (beforeWeight + afterWeight) / (beforeWeight / before + afterWeight / after)
  }

  /*
   * Keep the two endpoint tangents of each segment inside the monotone circle. The harmonic
   * mean already lands there in ordinary data; this also protects the end points and extreme
   * spacing ratios.
   */
  for (const [index, slope = 0] of slopes.entries()) {
    if (slope === 0) {
      tangents[index] = 0
      tangents[index + 1] = 0
      continue
    }
    const fromRatio = (tangents[index] ?? 0) / slope
    const toRatio = (tangents[index + 1] ?? 0) / slope
    const magnitude = fromRatio * fromRatio + toRatio * toRatio
    if (magnitude > 9) {
      const scale = 3 / Math.sqrt(magnitude)
      tangents[index] = scale * fromRatio * slope
      tangents[index + 1] = scale * toRatio * slope
    }
  }

  return pairs.map(({ from, to }, index) => {
    const width = to.x - from.x
    const low = Math.min(from.y, to.y)
    const high = Math.max(from.y, to.y)
    return {
      from,
      fromControl: {
        x: from.x + width / 3,
        y: clamp(from.y + (tangents[index] ?? 0) * (width / 3), low, high),
      },
      toControl: {
        x: to.x - width / 3,
        y: clamp(to.y - (tangents[index + 1] ?? 0) * (width / 3), low, high),
      },
      to,
    }
  })
}

const at = (value: number) => String(Math.round(value * 100) / 100)

export function curveThrough(points: readonly CurvePoint[]): string {
  const first = points[0]
  if (!first) {
    return ''
  }
  let path = `M ${at(first.x)} ${at(first.y)}`
  for (const segment of curveSegments(points)) {
    path +=
      ` C ${at(segment.fromControl.x)} ${at(segment.fromControl.y)},` +
      ` ${at(segment.toControl.x)} ${at(segment.toControl.y)},` +
      ` ${at(segment.to.x)} ${at(segment.to.y)}`
  }
  return path
}

/**
 * The same curve, split in two by what each stretch is: the ones running between days that
 * were actually logged, and the ones crossing a silence. Consecutive stretches of a kind are
 * joined into one subpath rather than drawn one at a time, so a run of days has no seam where
 * two segments meet.
 *
 * `guessed(index)` is asked about the stretch between points `index` and `index + 1`.
 */
export function curveSplitBy(
  points: readonly CurvePoint[],
  guessed: (index: number) => boolean,
): { solid: string; guessed: string } {
  const paths = { solid: '', guessed: '' }
  let open: 'solid' | 'guessed' | null = null

  for (const [index, segment] of curveSegments(points).entries()) {
    const kind = guessed(index) ? 'guessed' : 'solid'
    if (kind !== open) {
      paths[kind] += `${paths[kind] === '' ? '' : ' '}M ${at(segment.from.x)} ${at(segment.from.y)}`
      open = kind
    }
    paths[kind] +=
      ` C ${at(segment.fromControl.x)} ${at(segment.fromControl.y)},` +
      ` ${at(segment.toControl.x)} ${at(segment.toControl.y)},` +
      ` ${at(segment.to.x)} ${at(segment.to.y)}`
  }
  return paths
}
